import React, { useState, useMemo, useCallback, useRef } from 'react'
import { TextField, Typography, Grid, IconButton, InputAdornment, Divider } from '@material-ui/core'
import { FileCopy } from '@material-ui/icons'

const DEFAULT_DUTY_PCT = '35'
const DEFAULT_FEE_PCT = '10'
const DEFAULT_FEE_VALUE = '2.00'

export default function DutyAndFees() {
  const [itemValue, setItemValue] = useState('')
  const [shippingCost, setShippingCost] = useState('')
  const [dutyPct, setDutyPct] = useState(DEFAULT_DUTY_PCT)
  const [feePct, setFeePct] = useState(DEFAULT_FEE_PCT)
  const [feeValue, setFeeValue] = useState(DEFAULT_FEE_VALUE)

  const labelRef = useRef<HTMLInputElement>(null)
  const totalRef = useRef<HTMLInputElement>(null)

  const calculation = useMemo(() => {
    const item = parseFloat(itemValue) || 0
    const shipping = parseFloat(shippingCost) || 0
    const dutyRate = (parseFloat(dutyPct) || 0) / 100
    const feeRate = (parseFloat(feePct) || 0) / 100
    const flatFee = parseFloat(feeValue) || 0

    const duties = item * dutyRate
    const zononsFee = duties * feeRate + flatFee
    const dutiesAndFees = duties + zononsFee
    const total = dutiesAndFees + shipping

    return { duties, zononsFee, dutiesAndFees, total, shipping }
  }, [itemValue, shippingCost, dutyPct, feePct, feeValue])

  const hasInput = itemValue !== '' && shippingCost !== ''

  const handleCopy = useCallback((ref: React.RefObject<HTMLInputElement>) => {
    if (!ref.current) return
    ref.current.select()
    document.execCommand('copy')
    ref.current.setSelectionRange(0, 0)
  }, [])

  const copyAdornment = (ref: React.RefObject<HTMLInputElement>) => (
    <InputAdornment position="end">
      <IconButton size="small" onClick={() => handleCopy(ref)}>
        <FileCopy fontSize="small" />
      </IconButton>
    </InputAdornment>
  )

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5">Duty &amp; Fees</Typography>
      </Grid>

      {/* Required inputs */}
      <Grid item xs={12} sm={6}>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          label="Item Value"
          type="number"
          InputLabelProps={{ shrink: true }}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          value={itemValue}
          onChange={e => setItemValue(e.target.value)}
          placeholder="e.g. 91.98"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          label="Shipping Cost"
          type="number"
          InputLabelProps={{ shrink: true }}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          value={shippingCost}
          onChange={e => setShippingCost(e.target.value)}
          placeholder="e.g. 20.00"
        />
      </Grid>

      {/* Adjustable defaults */}
      <Grid item xs={12}>
        <Typography variant="caption" color="textSecondary">
          Defaults (Zonos)
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          label="Duty %"
          type="number"
          InputLabelProps={{ shrink: true }}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          value={dutyPct}
          onChange={e => setDutyPct(e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          label="Zonos Fee %"
          type="number"
          InputLabelProps={{ shrink: true }}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          value={feePct}
          onChange={e => setFeePct(e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          label="Zonos Flat Fee"
          type="number"
          InputLabelProps={{ shrink: true }}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          value={feeValue}
          onChange={e => setFeeValue(e.target.value)}
        />
      </Grid>

      {/* Output */}
      {hasInput && (
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Breakdown</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              Duties: <strong>${calculation.duties.toFixed(2)}</strong>
              {' '}({itemValue} × {dutyPct}%)
            </Typography>
            <Typography variant="body2">
              Zonos Fee: <strong>${calculation.zononsFee.toFixed(2)}</strong>
              {' '}({calculation.duties.toFixed(2)} × {feePct}% + ${feeValue})
            </Typography>
            <Typography variant="body2">
              Canada Post Shipping: <strong>${calculation.shipping.toFixed(2)}</strong>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              label="Shipping Line Name"
              InputLabelProps={{ shrink: true }}
              value="Shipping, Duties, and Fees"
              inputRef={labelRef}
              InputProps={{
                readOnly: true,
                endAdornment: copyAdornment(labelRef),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              label="Shipping, Duties, and Fees Total"
              InputLabelProps={{ shrink: true }}
              value={calculation.total.toFixed(2)}
              inputRef={totalRef}
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                endAdornment: copyAdornment(totalRef),
              }}
            />
          </Grid>
        </>
      )}
    </Grid>
  )
}
